/* Copyright (c) 2026 Xperi. All Rights Reserved. */

// Imported from the concrete module rather than the `../logging` barrel:
// clearAppStorage runs on the fresh-install boot path, before the Sage /
// diagnostic loggers the barrel also pulls in have been initialised.
import { Log } from "../logging/Log";
import { DB_NAME } from "../storage/storageConfig";

const TAG: string = "clearAppStorage";

/**
 * Clears all client-side storage belonging to this origin:
 * localStorage, sessionStorage, and every IndexedDB database.
 *
 * Used on a fresh-install launch (`freshInstall=true` from the
 * LG shell) so that stale iframe-origin data does not survive
 * an uninstall / reinstall cycle.
 */
export async function clearAppStorage(): Promise<void> {
  try {
    window.localStorage.clear();
  } catch (error: unknown) {
    Log.w(TAG, "Failed to clear localStorage", error);
  }

  try {
    window.sessionStorage.clear();
  } catch (error: unknown) {
    Log.w(TAG, "Failed to clear sessionStorage", error);
  }

  await clearIndexedDB();
}

function deleteDatabase(dbName: string): Promise<void> {
  return new Promise<void>((resolve: () => void): void => {
    const request: IDBOpenDBRequest = window.indexedDB.deleteDatabase(dbName);
    request.onsuccess = (): void => {
      resolve();
    };
    request.onerror = (): void => {
      Log.w(TAG, "Failed to delete IndexedDB: " + dbName);
      resolve();
    };
    request.onblocked = (): void => {
      Log.w(TAG, "IndexedDB delete blocked: " + dbName);
      resolve();
    };
  });
}

async function clearIndexedDB(): Promise<void> {
  if (!("indexedDB" in window)) {
    return;
  }

  const indexedDBWithDatabases = window.indexedDB as IDBFactory & {
    databases?: () => Promise<Array<{ name?: string }>>;
  };

  if (
    indexedDBWithDatabases.databases === null ||
    indexedDBWithDatabases.databases === undefined
  ) {
    // LG webOS WebKit does not implement indexedDB.databases().
    // The shell app stores all web-app data in a single DB (DB_NAME),
    // so delete it directly by name as a fallback.
    Log.w(
      TAG,
      "indexedDB.databases() not supported — deleting known DB by name"
    );
    await deleteDatabase(DB_NAME);
    return;
  }

  try {
    const databases: Array<{ name?: string }> =
      await indexedDBWithDatabases.databases();

    await Promise.all(
      databases.map((db: { name?: string }): Promise<void> => {
        if (db.name === null || db.name === undefined) {
          return Promise.resolve();
        }
        return deleteDatabase(db.name);
      })
    );
  } catch (error: unknown) {
    Log.w(TAG, "Failed to clear IndexedDB", error);
  }
}
